import {AiToolImpl} from "@me8eon/aiclient";
import {
    ElasticSearchConfig,
    ElasticSearchDocSearchResponse,
} from "@me8eon/ai.tools.utils";
import {ElasticsearchNexthinkDocument} from "./model/elasticsearchResponse";
import {fetchDeviceDetails, fetchSustainableITReport} from "./model/nexthinkApi";
import {NexthinkActionWidgetMeta} from "./model/nexthinkWidget";
import {JwtTokenFn} from "@me8eon/authentication";
import {EnvironmentConfig} from "@me8eon/config";

type ToolArgs = {
    elasticsearchDocId: string;
    query: string;
}

type ToolImpl = AiToolImpl<ToolArgs>

type Tool = (esConfig: ElasticSearchConfig, jwtTokenFn: JwtTokenFn) => ToolImpl

const TIMEZONE_FALLBACK_RESPONSE = "I can help you fix your system time and timezone settings. Please click on the button below to proceed.";

function normalizeLanguageCode(value?: string): string | undefined {
    if (!value) return undefined;
    const normalized = value.toLowerCase().slice(0, 2);
    if (normalized === "cs") return "cz";
    const supported = new Set(["en", "de", "sv", "ro", "cz", "pl", "it", "hu", "nl"]);
    return supported.has(normalized) ? normalized : undefined;
}

const mapElasticSearchDocumentsToWidgetInfoData = (documents: ElasticsearchNexthinkDocument, indexName: string): NexthinkActionWidgetMeta['props']['document'] => {
    return {
        title: documents._source.title || 'Untitled Action',
        category: documents._source.category || 'general',
        response: documents._source.response || ((documents._source.category === 'timezone') ? TIMEZONE_FALLBACK_RESPONSE : ''),
        elasticsearchDocUri: `${indexName}/_doc/${documents._id}`,
    }
}

export const DisplayNexthinkActionToolImpl: Tool = (esConfig, jwtTokenFn) => {
    return {
        type: "nextthink",
        toolDefn: {
            type: 'function',
            name: "display_nexthink_action",
            description: "This tool displays a Nexthink action based on provided Elasticsearch document ID. It retrieves the action details from Elasticsearch based on DocumentId and associated devices, returning the information as a custom Widget or string.",
            parameters: {
                type: "object",
                required: ["elasticsearchDocId", "query"],
                additionalProperties: false,
                properties: {
                    elasticsearchDocId: {
                        type: "string",
                        description: "The Elasticsearch document ID to look up the Nextthink action.",
                    },
                    query: {
                        type: "string",
                        description: "The query user asked previously to fetch the nexthink action.",
                    },
                    queryLanguage: {
                        type: "string",
                        description: "Optional ISO-like language code for the user's query (for example: en, de, sv, ro, cz, pl, it, hu, nl). Pass this when available.",
                    }
                }
            },
        },
        execute: async (indices, {elasticsearchDocId, query, queryLanguage}) => {
            try {
                const {
                    axios,
                } = esConfig;
                if (!elasticsearchDocId || elasticsearchDocId.trim().length === 0) throw new Error("No elasticsearchDocId provided to nexthink tool")
                
                let selectedIndexFromDocId: string | undefined;
                let actualDocId = elasticsearchDocId;
                if (elasticsearchDocId.includes('/')) {
                    const parts = elasticsearchDocId.split('/');
                    selectedIndexFromDocId = parts[0];
                    actualDocId = parts.slice(1).join('/');
                }

                const headers = {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${await jwtTokenFn()}`,
                };

                let allIndices: string[];
                if (indices && indices.length > 0) {
                    if (indices.length > 1) {
                        allIndices = indices;
                    } else {
                        allIndices = indices[0].split(',').map(i => i.trim());
                    }
                } else {
                    allIndices = ["nexthink-prod", "nexthink-teams-prod"];
                }
                let selectedIndex = selectedIndexFromDocId;
                
                if (selectedIndex) {
                    allIndices = [selectedIndex, ...allIndices.filter(idx => idx !== selectedIndex)];
                }
                
                let nexthinkIndex = allIndices[0];
                let elasticsearchDocumentDetailsResponse;
                let foundInIndex = false;

                for (const testIndex of allIndices) {
                    const trimmedIndex = testIndex.trim();
                    const testUrl = `${EnvironmentConfig.elasticSearchProxyUrl}/${trimmedIndex}/_doc/${actualDocId}`;
                    try {
                        elasticsearchDocumentDetailsResponse = await axios.get<ElasticSearchDocSearchResponse>(testUrl, {headers: headers});
                        if (elasticsearchDocumentDetailsResponse?.data?.found) {
                            nexthinkIndex = trimmedIndex;
                            foundInIndex = true;
                            break;
                        }
                    } catch (err) {
                        continue;
                    }
                }

                if (!foundInIndex || !elasticsearchDocumentDetailsResponse?.data?.found) {
                    return {
                        value: "The requested Nexthink action was not found. You MUST now use the knn tool to search the knowledge base for troubleshooting steps related to the user's query. Do NOT offer to create an incident yet - first try the knowledge base.",
                        _meta: { fallbackToKnn: true }
                    };
                }

                const elasticDoc = elasticsearchDocumentDetailsResponse.data as unknown as ElasticsearchNexthinkDocument;

                const disabledCategories = ['repair-O365-apps'];
                if (disabledCategories.includes(elasticDoc._source.category)) {
                    return {
                        value: "This Nexthink action is temporarily unavailable. You MUST now use the knn tool to search the knowledge base for troubleshooting steps related to the user's query. Do NOT offer to create an incident yet - first try the knowledge base.",
                        _meta: { fallbackToKnn: true }
                    };
                }

                const deviceResponse = await fetchDeviceDetails(jwtTokenFn);

                if (!deviceResponse?.data || deviceResponse.data.length === 0) {
                    return {
                        value: "No active device associated with Nexthink found hence unable to display the Nexthink action. You MUST now call the 'semanticHybridSearch' tool to search the knowledge base for troubleshooting steps related to the user's query. Do NOT offer to create an incident yet - first try the knowledge base.",
                    }
                }

                
                let widgetName = "nextthink_action";
                const category = elasticDoc._source?.category || '';
                if (category === "sustainable-it") {
                    widgetName = "nexthink_sustainableit";
                } else if (category === "nexthink-smartsolution") {
                    widgetName = "nexthink_smartsolution";
                }

                const widgetMeta: NexthinkActionWidgetMeta = {
                    name: widgetName,
                    title: "Nextthink Actions",
                    description: "Displays Nexthink actions for the user.",
                    type: "widget",
                    componentName: "NexthinkActionsWidget",
                    props: {
                        devices: deviceResponse?.data || [],
                        document: mapElasticSearchDocumentsToWidgetInfoData(elasticDoc, nexthinkIndex),
                        query: query || '',
                        queryLanguage: normalizeLanguageCode(queryLanguage),
                    },
                    displaySettings: {
                        mode: "inThisSessionOnly",
                        sessionId: "somesessionid"
                    }
                }

                return {
                    _meta: {
                        widget: widgetMeta,
                        skipAiSummary: true,
                    },
                    value: "Nexthink widget has been displayed to the user."
                }
            } catch (error) {
                console.error("Error executing DisplayNexthinkActionToolImpl:", error);
                return {
                    value: "Nexthink widget has not been displayed to the user. An error occurred while fetching the Nexthink action details. User should be informed accordingly."
                }
            }
        }
    }
}
